import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Col, DatePicker, Descriptions, Form, Input, InputNumber, message, Modal,
  Row, Select, Space, Statistic
} from 'antd';
import { ExportOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fixedAssetsApi } from './fixedAssetsApi';
import {
  DISPOSAL_STATUSES, findDisposalFormErrors, friendlyErrorFor,
  calculateDisposalPreview, formatRupee
} from './fixedAssetsHelpers';
import '../accounts.css';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Fixed Asset Disposal modal (Stage 6B.9D).
//
// Operator enters disposal date + sale value + receiving account →
// modal shows live gain/loss preview → Post (Approval-MFA L1).
// ─────────────────────────────────────────────────────────────────

function LedgerSelect({ value, onChange, disabled, subtype, placeholder }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  const fetcher = useCallback(async (q) => {
    setLoading(true);
    try {
      if (subtype) return await fixedAssetsApi.ledgersBySubtype(subtype, { q, limit: 100 });
      return await fixedAssetsApi.ledgerLookup(q || '', 100);
    } finally { setLoading(false); }
  }, [subtype]);
  const onOpen = useCallback(async (open) => {
    if (!open || options.length > 0) return;
    try { setOptions(await fetcher('')); } catch (_e) { /* */ }
  }, [options.length, fetcher]);
  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try { setOptions(await fetcher(text || '')); } catch (_e) { /* */ }
    }, 200);
  }, [fetcher]);
  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      showSearch
      placeholder={placeholder || 'Search ledger…'}
      value={value || undefined}
      onChange={(v) => onChange(v || null)}
      onDropdownVisibleChange={onOpen}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      allowClear
      notFoundContent={loading ? 'Loading…' : 'No ledgers'}
      options={options.map((o) => ({
        value: o.id,
        label: <span>
          <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{o.accountCode}</code>
          {o.accountName}
        </span>
      }))}
    />
  );
}

export default function FixedAssetDisposalModal({
  open, asset, onClose, onDisposed, runWithApproval
}) {
  const [form] = Form.useForm();
  const [saleValue, setSaleValue] = useState(0);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      disposalDate: dayjs(),
      saleValue:    0,
      status:       'sold',
      receivingAccountId: null,
      reason:       ''
    });
    setSaleValue(0);
  }, [open, form]);

  const preview = useMemo(() => {
    if (!asset) return null;
    return calculateDisposalPreview({
      originalCost:            asset.originalCost,
      additionalCost:          asset.additionalCost,
      accumulatedDepreciation: asset.accumulatedDepreciation,
      saleValue
    });
  }, [asset, saleValue]);

  const runDispose = useCallback(async () => {
    if (!asset) return;
    try {
      const values = await form.validateFields();
      const body = {
        disposalDate:        values.disposalDate.format('YYYY-MM-DD'),
        saleValue:           Number(values.saleValue) || 0,
        status:              values.status || 'sold',
        receivingAccountId:  values.receivingAccountId || null,
        reason:              values.reason || null
      };
      const errs = findDisposalFormErrors(body);
      if (errs.length) { message.error(errs.join(' ')); return; }
      const confirmed = await new Promise((resolve) => {
        let okClicked = false;
        const gainLossText = preview && preview.gainLossOnDisposal > 0
          ? `gain of ₹ ${formatRupee(preview.gainLossOnDisposal)}`
          : preview && preview.gainLossOnDisposal < 0
            ? `loss of ₹ ${formatRupee(Math.abs(preview.gainLossOnDisposal))}`
            : 'no gain / loss';
        Modal.confirm({
          title: `Dispose ${asset.assetCode}?`,
          icon:  <CheckCircleOutlined style={{ color: '#3f8600' }} />,
          content: `This will post a disposal JV through the engine (${gainLossText}). You will be asked for a 6-digit Approval MFA code to confirm.`,
          okText: 'Dispose', okButtonProps: { danger: true },
          cancelText: 'Cancel',
          onOk:    () => { okClicked = true; },
          onCancel: () => { okClicked = false; },
          afterClose: () => resolve(okClicked)
        });
      });
      if (!confirmed) return;
      setPosting(true);
      await runWithApproval({
        operationKey:   'accounts.fixed_asset.dispose',
        resourceType:   'fixed_asset',
        resourceId:     asset.id,
        operationLabel: 'Dispose fixed asset',
        resourceLabel:  `${asset.assetCode} · ${asset.assetName}`,
        action: (approvalMfaChallengeId) =>
          fixedAssetsApi.dispose(asset.id, body, { approvalMfaChallengeId })
      });
      message.success('Asset disposed.');
      onDisposed && onDisposed();
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') return;
      if (e && e.errorFields) return;
      message.error(friendlyErrorFor(e));
    } finally { setPosting(false); }
  }, [asset, form, preview, runWithApproval, onDisposed]);

  if (!asset) return null;

  return (
    <Modal
      title={`Dispose ${asset.assetCode} · ${asset.assetName}`}
      open={open}
      onCancel={onClose}
      width={720}
      centered
      destroyOnClose
      maskClosable={false}
      footer={
        <Space>
          <Button type="primary" danger icon={<ExportOutlined />}
            loading={posting} onClick={runDispose}>
            Post Disposal
          </Button>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
      className="erp-accounts-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', padding: '12px 16px' } }}
    >
      <Form form={form} layout="vertical" preserve className="erp-accounts-form"
        onValuesChange={(_changed, all) => {
          if ('saleValue' in (_changed || {})) {
            setSaleValue(Number(all.saleValue) || 0);
          }
        }}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Disposal Date" name="disposalDate"
              rules={[{ required: true, message: 'Disposal date is required.' }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Status" name="status"
              rules={[{ required: true, message: 'Disposal status is required.' }]}>
              <Select options={DISPOSAL_STATUSES.map((s) => ({ value: s.value, label: s.label }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Sale Value (₹)" name="saleValue">
              <InputNumber min={0} step={100} style={{ width: '100%' }}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Receiving Account (required if Sale Value > 0)" name="receivingAccountId">
          <LedgerSelect placeholder="Cash / Bank / Sundry Debtor ledger" />
        </Form.Item>
        <Form.Item label="Reason / Remarks" name="reason">
          <Input.TextArea rows={2} maxLength={2000}
            placeholder="e.g. End-of-life scrap; sold to ABC Recycling" />
        </Form.Item>
      </Form>

      {preview ? (
        <div style={{ marginTop: 12 }}>
          <Row gutter={[10, 10]}>
            <Col xs={12} md={6}>
              <Statistic title="WDV (₹)" value={preview.wdv} precision={2} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Sale Value (₹)" value={preview.saleValue} precision={2} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={preview.gainLossOnDisposal >= 0 ? 'Gain (₹)' : 'Loss (₹)'}
                value={Math.abs(preview.gainLossOnDisposal)} precision={2}
                valueStyle={{ color: preview.gainLossOnDisposal >= 0 ? '#3f8600' : '#cf1322' }} />
            </Col>
            <Col xs={12} md={6}>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Asset code">{asset.assetCode}</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
          <Alert type="info" showIcon style={{ marginTop: 10 }}
            message="The disposal JV will be posted through the engine"
            description={
              <span>
                DR Accumulated Depreciation ({formatRupee(asset.accumulatedDepreciation || 0)})
                {' '}DR Receiving Account ({formatRupee(preview.saleValue)})
                {' '}{preview.isLoss ? `DR Loss on Disposal (${formatRupee(Math.abs(preview.gainLossOnDisposal))})` : ''}
                {' '}CR Fixed Asset Ledger ({formatRupee(Number(asset.originalCost || 0) + Number(asset.additionalCost || 0))})
                {' '}{preview.isGain ? `CR Gain on Disposal (${formatRupee(preview.gainLossOnDisposal)})` : ''}
              </span>
            } />
        </div>
      ) : null}
    </Modal>
  );
}
